import { snakeCase } from "lodash";

import {
  characterActions,
  characterSelectors,
  Constants,
  RuleData,
  RuleDataUtils,
} from "@dndbeyond/character-rules-engine/es";

import type { ClickElementMapper } from "../middleware";
import type { ClickElementInput } from "../pipeline";

type PreferenceChooseAction = ReturnType<
  typeof characterActions.preferenceChoose
>;

export const SETTINGS_PAGE_CATEGORY = "builder_settings";

type SettingControl =
  | { elementType: "toggle" }
  | { elementType: "dropdown" | "radio"; options: Record<number, string> };

const TOGGLE: SettingControl = { elementType: "toggle" };

// Preferences on the builder's Home/Basic page, with the option labels it shows
const SETTINGS: Record<string, SettingControl> = {
  useHomebrewContent: TOGGLE,
  enableOptionalClassFeatures: TOGGLE,
  enableOptionalOrigins: TOGGLE,
  enforceFeatRules: TOGGLE,
  enforceMulticlassRules: TOGGLE,
  showScaledSpells: TOGGLE,
  ignoreCoinWeight: TOGGLE,
  progressionType: {
    elementType: "dropdown",
    options: {
      [Constants.PreferenceProgressionTypeEnum.MILESTONE]: "Milestone",
      [Constants.PreferenceProgressionTypeEnum.XP]: "XP",
    },
  },
  hitPointType: {
    elementType: "dropdown",
    options: {
      [Constants.PreferenceHitPointTypeEnum.FIXED]: "Fixed",
      [Constants.PreferenceHitPointTypeEnum.MANUAL]: "Manual",
    },
  },
  encumbranceType: {
    elementType: "dropdown",
    options: {
      [Constants.PreferenceEncumbranceTypeEnum.ENCUMBRANCE]: "Use Encumbrance",
      [Constants.PreferenceEncumbranceTypeEnum.NONE]: "No Encumbrance",
      [Constants.PreferenceEncumbranceTypeEnum.VARIANT]: "Variant Encumbrance",
    },
  },
  abilityScoreDisplayType: {
    elementType: "dropdown",
    options: {
      [Constants.PreferenceAbilityScoreDisplayTypeEnum.MODIFIERS_TOP]:
        "Modifiers Top",
      [Constants.PreferenceAbilityScoreDisplayTypeEnum.SCORES_TOP]:
        "Scores Top",
    },
  },
  privacyType: {
    elementType: "radio",
    options: {
      [Constants.PreferencePrivacyTypeEnum.CAMPAIGN_ONLY]: "Campaign Only",
      [Constants.PreferencePrivacyTypeEnum.PUBLIC]: "Public",
      [Constants.PreferencePrivacyTypeEnum.PRIVATE]: "Private",
    },
  },
};

/**
 * A setting toggled on or off, in the same shape as phandalin's settings
 * page: "turned_<itemName>_on" / "turned_<itemName>_off"
 */
export const getToggleClick = (
  itemName: string,
  isOn: boolean,
  elementId?: string
): ClickElementInput => ({
  elementName: itemName,
  elementType: "toggle",
  elementId,
  pageCategory: SETTINGS_PAGE_CATEGORY,
  details: {
    itemName,
    actionDetail: `turned_${itemName}_${isOn ? "on" : "off"}`,
  },
});

/**
 * A setting changed on the Home/Basic page
 */
export const mapPreferenceChoose: ClickElementMapper<PreferenceChooseAction> = (
  action,
  prevState
) => {
  const { key, value } = action.payload;
  const setting = SETTINGS[key];
  if (!setting) return [];

  const previous = characterSelectors.getPreferences(prevState)?.[key];
  if (previous === value) return [];

  const itemName = `${snakeCase(key)}_setting`;
  if (setting.elementType === "toggle") {
    return [getToggleClick(itemName, Boolean(value))];
  }

  const label = setting.options[Number(value)] ?? String(value);
  return [
    {
      elementName: itemName,
      elementType: setting.elementType,
      pageCategory: SETTINGS_PAGE_CATEGORY,
      details: {
        itemName,
        actionDetail: `set_${itemName}_${snakeCase(label)}`,
      },
    },
  ];
};

/**
 * A source or partnered content checkbox toggled on the Home/Basic page,
 * named after its source category, with the category id as ELEMENT_ID
 */
export const getSourceCategoryClick = (
  sourceCategoryId: number,
  isOn: boolean,
  ruleData: RuleData
): ClickElementInput => {
  const name =
    RuleDataUtils.getSourceCategoryLookup(ruleData)?.[sourceCategoryId]?.name ??
    String(sourceCategoryId);
  return getToggleClick(
    `source_category_${snakeCase(name)}_setting`,
    isOn,
    String(sourceCategoryId)
  );
};

/**
 * The "Enable All Partnered Content" checkbox, sent as one event rather than
 * one per partner
 */
export const getEnableAllPartneredContentClick = (
  isOn: boolean
): ClickElementInput =>
  getToggleClick("enable_all_partnered_content_setting", isOn);
